import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Zap, ShieldCheck, CheckCircle2, MapPin, Mail, Phone } from "lucide-react";
import { api, imageSourceFor } from "@/lib/api";
import { Card, SectionLabel } from "@/components/mo";

function ContactRow({ icon: Icon, text, href }) {
  const content = (
    <div className="flex items-center gap-3 py-2 font-body text-sm text-mo-ink">
      <Icon className="h-4 w-4 text-mo-brand" />
      {text}
    </div>
  );
  return href ? (
    <a href={href} target="_blank" rel="noreferrer" className="block hover:text-mo-brand">
      {content}
    </a>
  ) : (
    content
  );
}

export default function Studio() {
  const { data: studio } = useQuery({ queryKey: ["studio"], queryFn: api.getStudio });

  return (
    <div className="mo-rise">
      <section className="relative h-72 overflow-hidden md:rounded-b-3xl">
        {studio ? (
          <img
            src={imageSourceFor({ imagePath: studio.studioImagePath, imageUrl: studio.studioImageUrl })}
            alt=""
            data-testid="studio-main-image"
            className="absolute inset-0 h-full w-full object-cover"
          />
        ) : null}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-mo-surface/40 to-mo-surface" />
        <div className="absolute inset-x-0 bottom-0 flex flex-col items-center pb-6 text-center">
          <span className="font-display text-3xl font-bold tracking-[0.1em] text-mo-ink">
            STUDIO {(studio?.name || "MÖRPHÖSY").toUpperCase()}
          </span>
          <p className="font-body text-[13px] text-mo-muted">Arte, Anatomia & Trasformazione sulla Pelle</p>
        </div>
      </section>

      <div className="flex flex-col gap-6 px-5 py-6 md:px-10">
        <Card>
          <p className="whitespace-pre-line font-body text-sm leading-relaxed text-mo-ink">{studio?.description}</p>
        </Card>

        <div>
          <SectionLabel>Flash Tattoo Disponibili</SectionLabel>
          <p className="mb-3 mt-1 font-body text-xs text-mo-muted">Una selezione dei nostri progetti pronti.</p>
          <div className="flex flex-col gap-3">
            {(studio?.flashes || []).map((f, i) => (
              <div
                key={i}
                data-testid={`flash-${i}`}
                className="flex items-center gap-4 rounded-xl border border-mo-line bg-mo-card p-3"
              >
                <img src={imageSourceFor(f)} alt={f.title} className="h-18 w-18 rounded-lg object-cover" style={{ height: 72, width: 72 }} />
                <div className="flex-1">
                  <p className="font-body text-[15px] font-bold text-mo-ink">{f.title}</p>
                  <p className="font-body text-xs text-mo-muted">{f.subtitle}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <Card>
          <div className="flex items-center gap-2">
            <Zap className="h-4 w-4 text-mo-ink" />
            <SectionLabel>Integrazione AI · Consulenza & Mockup</SectionLabel>
          </div>
          <div className="mt-3 flex items-center gap-2 rounded-lg bg-mo-surface p-3">
            <CheckCircle2 className="h-4 w-4 text-mo-success" />
            <span className="font-body text-[13px] text-mo-ink">Motore di analisi anatomica attivo e connesso</span>
          </div>

          <div className="mt-5 flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-mo-ink" />
            <SectionLabel>Standard Igienico-Sanitari</SectionLabel>
          </div>
          <p className="mt-2 whitespace-pre-line font-body text-sm leading-relaxed text-mo-ink">{studio?.hygiene}</p>
        </Card>

        <Card>
          <SectionLabel>Contatti dello Studio</SectionLabel>
          <div className="mt-1">
            <ContactRow icon={MapPin} text={studio?.address} />
            {studio?.email ? <ContactRow icon={Mail} text={studio.email} href={`mailto:${studio.email}`} /> : null}
            {studio?.whatsapp ? (
              <ContactRow icon={Phone} text={`WhatsApp ${studio?.phone || ""}`} href={`https://wa.me/${studio.whatsapp}`} />
            ) : null}
          </div>
        </Card>
      </div>
    </div>
  );
}
